/**
 * Platform-based sidebar menu config.
 * - Main items with subMenu: open right panel with categorized sub-items.
 * - Main items with link only (no subMenu): direct navigation; subMenu can be added later.
 *
 * "New" badge (sidebar rail + sub-panel):
 * - Set `isNew: true` on a main menu item and/or any sub-menu item.
 * - Optional `newLabel` overrides the default text (default: "New").
 * - Main rail shows a badge when the item is new or any of its sub-items are new.
 * - `badge: true` is separate — red dot for notifications (e.g. history-logs).
 *
 * Query-based sub-routes (same path, different query):
 * - Define each variant as a separate item with full link including query, e.g.:
 *   link: "product-analytics"
 *   link: "product-analytics?reportType=child_product&viewtype=PERFORMANCE"
 *   link: "product-analytics?reportType=child_product&viewtype=FUNNEL"
 * - Or add `alternateLinks` on an item when the same feature opens on a shared path:
 *   alternateLinks: ["product-analytics?page=stoploss"]
 * - getActiveMainId() / getActiveSubId() match path + search and pick the most specific match.
 */
/*MUI ICON IMPORTS */
import SpaceDashboardOutlined from "@mui/icons-material/SpaceDashboardOutlined";
import TrendingUpOutlined from "@mui/icons-material/TrendingUpRounded";
import SpeedOutlined from "@mui/icons-material/SpeedOutlined";
import TipsAndUpdatesOutlined from "@mui/icons-material/TipsAndUpdatesOutlined";
import ElectricBoltRounded from "@mui/icons-material/BoltRounded";
import NotificationsNoneRounded from "@mui/icons-material/NotificationsNoneRounded";
import AppsRounded from "@mui/icons-material/AppsRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";

export const SIDEBAR_NEW_BADGE_DEFAULT_LABEL = "New";

/** Same path as product-analytics; distinguished by `page` query param. */
export const PRODUCT_ANALYTICS_STOPLOSS_LINK =
  "product-analytics?page=stoploss";
export const PRODUCT_ANALYTICS_PRODUCTSET_LINK =
  "product-analytics?page=productset";

/** True when a main menu item or any of its sub-items is marked as new. */
export function menuHasNewBadge(item) {
  if (!item) return false;
  if (item.isNew) return true;
  for (const cat of item.subMenu?.categories || []) {
    if ((cat.items || []).some((sub) => sub.isNew)) return true;
  }
  return false;
}

export function getNewBadgeLabel(item) {
  return item?.newLabel || SIDEBAR_NEW_BADGE_DEFAULT_LABEL;
}

export const SIDEBAR_MENU_CONFIG = {
  amazon: [
    {
      id: "dashboard",
      label: "Dashboard",
      link: "/",
      icon: SpaceDashboardOutlined,
      subMenu: null,
    },
    {
      id: "ppm",
      label: "PPM",
      icon: SpeedOutlined,
      subMenu: {
        categories: [
          {
            title: "PPM",
            items: [
              {
                id: "product-set-list",
                name: "Product Set List",
                link: "product-set-list",
                alternateLinks: [PRODUCT_ANALYTICS_PRODUCTSET_LINK],
              },
              {
                id: "stop-loss-list",
                name: "Stop Loss",
                link: "stop-loss-list",
                alternateLinks: [PRODUCT_ANALYTICS_STOPLOSS_LINK],
              },
              {
                id: "core-size",
                name: "Core Size",
                link: "core-size",
                isNew: true,
              },
            ],
          },
        ],
      },
    },
    {
      id: "product-insights",
      label: "Product Insights",
      icon: TrendingUpOutlined,
      subMenu: {
        categories: [
          {
            title: "Reports",
            items: [
              {
                id: "product-analytics",
                name: "Product Analytics",
                link: "product-analytics",
              },
              { id: "master-ads", name: "Product Ads", link: "master-ads" },
            ],
          },
        ],
      },
    },
    {
      id: "account-insights",
      label: "Account Insights",
      isNew: true,
      icon: TipsAndUpdatesOutlined,
      subMenu: {
        categories: [
          {
            title: "Optimizer",
            items: [
              { id: "campaign-list", name: "Campaigns", link: "campaign-list-v2", alternateLinks: ["campaign-list"] },
              { id: "targeting", name: "Targeting", link: "targeting-v2", alternateLinks: ["optimizer/targeting"] },
              { id: "bid-adjust", name: "Placement Bids", link: "bid-adjust-v2", alternateLinks: ["bid-adjust"] },
              { id: "search-term", name: "Search Term", link: "search-term-v2", alternateLinks: ["search-term"] },
            ],
          },
          {
            title: "Analytics",
            items: [
              { id: "account-overview", name: "Account overview", link: "account-overview" },
              { id: "marketing-stream", name: "Marketing Stream", link: "marketing-stream" },
            ],
          },
        ],
      },
    },
    {
      id: "account-automation",
      label: "Account Automation",
      icon: ElectricBoltRounded,
      subMenu: {
        categories: [
          {
            title: "Automation",
            items: [
              { id: "automations-create-rule", name: "Configure Bid Rules", link: "automations/create-rule?ruleFor=Improve%20Efficiency" },
              { id: "automations", name: "Bid Automation List", link: "automations" },
              { id: "automations-logs", name: "Bid Automation Logs", link: "automations/logs" },
              { id: "campaign-abo-list", name: "Campaign ABO", link: "campaign-abo-list", isNew: true },
            ],
          },
        ],
      },
    },
    {
      id: "history-logs",
      label: "Notification",
      icon: NotificationsNoneRounded,
      subMenu: {
        categories: [
          {
            title: "Logs",
            items: [
              { id: "request-tracking", name: "Request Tracking Logs", link: "request-tracking", isNew: true },
              { id: "history-logs", name: "History Logs", link: "history-logs", isNew: true },
            ],
          },
        ],
      },
      badge: true,
      pinnedBottom: true,
    },
    {
      id: "externals",
      label: "Externals",
      icon: AppsRounded,
      pinnedBottom: true,
      subMenu: {
        categories: [
          {
            title: "Administration",
            items: [
              {
                id: "admin-view",
                name: "Admin View",
                action: "OPEN_ADMIN",
                isExternal: true,
                showForMultiuserOnly: true,
              },
              {
                id: "cms",
                name: "CMS",
                action: "OPEN_CMS",
                isExternal: true,
              },
              {
                id: "setup",
                name: "Setup",
                link: "setup",
              },
            ],
          },
          {
            title: "Reports",
            items: [
              {
                id: "internal-bi",
                name: "Internal BI",
                action: "MARKETPLACE_BI",
                isExternal: true,
                showForMultiuserOnly: true,
              },
              {
                id: "afm-report",
                name: "AFM Report",
                action: "AFM_REPORT",
                isExternal: true,
              },
            ],
          },
        ],
      },
    },
    
    // {
    //   id: "integrations",
    //   label: "Integration",
    //   link: "/integrations",
    //   icon: SettingsRounded,
    //   subMenu: null,
    //   pinnedBottom: true,
    // },
    {
      id: "settings",
      label: "Settings",
      icon: SettingsRounded,
      pinnedBottom: true,
      subMenu: {
        categories: [
          {
            title: "General",
            items: [
              { id: "integrations", name: "Integrations", link: "integrations" },
              { id: "feed-mapping", name: "Feed Mapping", link: "feed-mapping" },
            ],
          },
        ],
      },
    },
  ],
  flipkart: [
    {
      id: "dashboard",
      label: "Dashboard",
      link: "/",
      subMenu: null,
      icon: SpaceDashboardOutlined,
    },
    {
      id: "ppm",
      label: "PPM",
      icon: SpeedOutlined,
      subMenu: {
        categories: [
          {
            title: "PPM",
            items: [
              {
                id: "product-set-list",
                name: "Product Set List",
                link: "product-set-list",
                alternateLinks: [PRODUCT_ANALYTICS_PRODUCTSET_LINK],
              },
              {
                id: "stop-loss-list",
                name: "Stop Loss",
                link: "stop-loss-list",
                isNew: true,
                alternateLinks: [PRODUCT_ANALYTICS_STOPLOSS_LINK],
              },
              {
                id: "core-size",
                name: "Core Size",
                link: "core-size",
                isNew: true,
              },
            ],
          },
        ],
      },
    },
    {
      id: "product-insights",
      label: "Product Insights",
      icon: TrendingUpOutlined,
      subMenu: {
        categories: [
          {
            title: "Reports",
            items: [
              {
                id: "product-analytics",
                name: "Product Analytics",
                link: "product-analytics",
              },
            ],
          },
        ],
      },
    },
    {
      id: "account-insights",
      label: "Account Insights",
      icon: TipsAndUpdatesOutlined,
      subMenu: {
        categories: [
          {
            title: "Reports",
            items: [
              { id: "performance-analysis", name: "Performance Analysis", link: "performance-analysis" },
            ],
          },
        ],
      },
    },
    {
      id: "history-logs",
      label: "Notification",
      icon: NotificationsNoneRounded,
      subMenu: {
        categories: [
          {
            title: "Logs",
            items: [
              { id: "request-tracking", name: "Request Tracking Logs", link: "request-tracking", isNew: true },
              { id: "history-logs", name: "History Logs", link: "history-logs", isNew: true },
            ],
          },
        ],
      },
      badge: true,
      pinnedBottom: true,
    },
    {
      id: "externals",
      label: "Externals",
      icon: AppsRounded,
      pinnedBottom: true,
      subMenu: {
        categories: [
          {
            title: "Reports",
            items: [
              {
                id: "internal-bi",
                name: "Internal BI",
                action: "MARKETPLACE_BI",
                isExternal: true,
                showForMultiuserOnly: true,
              },
              {
                id: "afm-report",
                name: "AFM Report",
                action: "AFM_REPORT",
                isExternal: true,
              },
            ],
          },
        ],
      },
    },
    // {
    //   id: "integrations",
    //   label: "Integration",
    //   link: "/integrations",
    //   icon: SettingsRounded,
    //   subMenu: null,
    //   pinnedBottom: true,
    // },
    {
      id: "settings",
      label: "Settings",
      icon: SettingsRounded,
      pinnedBottom: true,
      subMenu: {
        categories: [
          {
            title: "General",
            items: [
              { id: "integrations", name: "Integrations", link: "integrations" },
              { id: "feed-mapping", name: "Feed Mapping", link: "feed-mapping", isNew: true },
            ],
          },
        ],
      },
    },
  ],
  myntra: [
    {
      id: "dashboard",
      label: "Dashboard",
      link: "/",
      subMenu: null,
      icon: SpaceDashboardOutlined,
    },
    {
      id: "ppm",
      label: "PPM",
      icon: SpeedOutlined,
      subMenu: {
        categories: [
          {
            title: "PPM",
            items: [
              {
                id: "product-set-list",
                name: "Product Set List",
                link: "product-set-list",
                alternateLinks: [PRODUCT_ANALYTICS_PRODUCTSET_LINK],
              },
              {
                id: "stop-loss-list",
                name: "Stop Loss",
                link: "stop-loss-list",
                isNew: true,
                alternateLinks: [PRODUCT_ANALYTICS_STOPLOSS_LINK],
              },
              {
                id: "core-size",
                name: "Core Size",
                link: "core-size",
                isNew: true,
              },
            ],
          },
        ],
      },
    },
    {
      id: "product-insights",
      label: "Product Insights",
      icon: TrendingUpOutlined,
      subMenu: {
        categories: [
          {
            title: "Reports",
            items: [
              {
                id: "product-analytics",
                name: "Product Analytics",
                link: "product-analytics",
              },
            ],
          },
        ],
      },
    },
    {
      id: "account-insights",
      label: "Account Insights",
      icon: TipsAndUpdatesOutlined,
      subMenu: {
        categories: [
          {
            title: "Reports",
            items: [
              { id: "performance-analysis", name: "Performance Analysis", link: "performance-analysis" },
            ],
          },
        ],
      },
    },
    {
      id: "history-logs",
      label: "Notification",
      icon: NotificationsNoneRounded,
      subMenu: {
        categories: [
          {
            title: "Logs",
            items: [
              { id: "request-tracking", name: "Request Tracking Logs", link: "request-tracking", isNew: true },
              { id: "history-logs", name: "History Logs", link: "history-logs", isNew: true },
            ],
          },
        ],
      },
      badge: true,
      pinnedBottom: true,
    },
    {
      id: "externals",
      label: "Externals",
      icon: AppsRounded,
      pinnedBottom: true,
      subMenu: {
        categories: [
          {
            title: "Reports",
            items: [
              {
                id: "internal-bi",
                name: "Internal BI",
                action: "MARKETPLACE_BI",
                link: "/reports?title=AFM+Report",
                isExternal: true,
                showForMultiuserOnly: true,
              },
              {
                id: "afm-report",
                name: "AFM Report",
                action: "AFM_REPORT",
                isExternal: true,
              },
            ],
          },
        ],
      },
    },
    // {
    //   id: "integrations",
    //   label: "Integration",
    //   link: "/integrations",
    //   icon: SettingsRounded,
    //   subMenu: null,
    //   pinnedBottom: true,
    // },
    {
      id: "settings",
      label: "Settings",
      icon: SettingsRounded,
      pinnedBottom: true,
      subMenu: {
        categories: [
          {
            title: "General",
            items: [
              { id: "integrations", name: "Integrations", link: "integrations" },
              { id: "feed-mapping", name: "Feed Mapping", link: "feed-mapping", isNew: true },
            ],
          },
        ],
      },
    },
  ],
};

export function withSavedReportsInMenu(
  menuConfig = [],
  {
    performanceSavedReportItems = [],
    productAnalyticsSavedReportItems = [],
  } = {}
) {
  if (!Array.isArray(menuConfig)) return [];
  return menuConfig.map((mainItem) => {
    if (mainItem?.id === "account-insights" && mainItem?.subMenu?.categories) {
      const categories = [...mainItem.subMenu.categories];
      const savedReportsCategoryIndex = categories.findIndex(
        (category) => category?.title === "Saved Reports"
      );
      const savedReportsCategory = {
        title: "Saved Reports",
        items: performanceSavedReportItems,
      };

      if (savedReportsCategoryIndex >= 0) {
        categories[savedReportsCategoryIndex] = savedReportsCategory;
      } else if (performanceSavedReportItems.length) {
        categories.push(savedReportsCategory);
      }

      return {
        ...mainItem,
        subMenu: {
          ...mainItem.subMenu,
          categories,
        },
      };
    }

    if (mainItem?.id === "product-insights" && mainItem?.subMenu?.categories) {
      const categories = [...mainItem.subMenu.categories];
      const savedReportsCategoryIndex = categories.findIndex(
        (category) => category?.title === "Saved Reports"
      );
      const savedReportsCategory = {
        title: "Saved Reports",
        items: productAnalyticsSavedReportItems,
      };

      if (savedReportsCategoryIndex >= 0) {
        categories[savedReportsCategoryIndex] = savedReportsCategory;
      } else if (productAnalyticsSavedReportItems.length) {
        categories.push(savedReportsCategory);
      }

      return {
        ...mainItem,
        subMenu: {
          ...mainItem.subMenu,
          categories,
        },
      };
    }

    return mainItem;
  });
}

/** Resolve active main menu id from pathname + search. Handles query-based routes. */
export function getActiveMainId(pathname, menuConfig, search = "") {
  if (!pathname || pathname === "/") return "dashboard";

  const pathOnly = pathname.replace(/^\//, "").split("?")[0];
  const searchNorm = search?.replace(/^\?/, "") || "";

  let bestMainId = null;
  let bestScore = -1;

  for (const main of menuConfig) {
    for (const link of collectMenuItemLinks(main)) {
      const score = getLinkMatchScore(pathOnly, searchNorm, link);
      if (score != null && score > bestScore) {
        bestScore = score;
        bestMainId = main.id;
      }
    }

    for (const cat of main.subMenu?.categories || []) {
      for (const item of cat.items || []) {
        for (const link of collectMenuItemLinks(item)) {
          const score = getLinkMatchScore(pathOnly, searchNorm, link);
          if (score != null && score > bestScore) {
            bestScore = score;
            bestMainId = main.id;
          }
        }
      }
    }
  }

  return bestMainId;
}


/**
 * Check if current URL search contains all params from item link query.
 * Param order in URL does not matter.
 */
function searchContains(currentSearch, itemQuery) {
  if (!itemQuery) return true;
  if (!currentSearch) return false;
  const cur = new URLSearchParams(currentSearch.replace(/^\?/, ""));
  const item = new URLSearchParams(itemQuery.replace(/^\?/, ""));
  for (const [k, v] of item) {
    if (cur.get(k) !== v) return false;
  }
  return true;
}

function collectMenuItemLinks(item) {
  const links = [];
  if (item?.link) links.push(item.link);
  if (Array.isArray(item?.alternateLinks)) {
    links.push(...item.alternateLinks);
  }
  return links;
}

function getLinkMatchScore(pathOnly, searchNorm, link) {
  if (!link) return null;

  const normalizedLink = link.replace(/^\//, "");
  const linkPath = normalizedLink.split("?")[0];
  const linkQuery = normalizedLink.includes("?")
    ? normalizedLink.split("?")[1]
    : "";

  const pathMatch =
    pathOnly === linkPath || pathOnly.startsWith(`${linkPath}/`);
  if (!pathMatch) return null;

  let score = linkPath.length;
  if (linkQuery) {
    if (!searchContains(searchNorm, linkQuery)) return null;
    score += new URLSearchParams(linkQuery).size * 10;
  }

  return score;
}

/** Resolve active sub item id from pathname + search. Handles query-based routes. */
export function getActiveSubId(pathname, menuConfig, search = "") {
  if (!pathname || pathname === "/") return null;

  const pathOnly = pathname.replace(/^\//, "").split("?")[0];
  const searchNorm = search?.replace(/^\?/, "") || "";

  let bestMatch = null;
  let bestScore = -1;

  for (const main of menuConfig) {
    if (!main.subMenu) continue;

    for (const cat of main.subMenu.categories || []) {
      for (const item of cat.items || []) {
        for (const link of collectMenuItemLinks(item)) {
          const score = getLinkMatchScore(pathOnly, searchNorm, link);
          if (score != null && score > bestScore) {
            bestScore = score;
            bestMatch = item.id;
          }
        }
      }
    }
  }

  return bestMatch;
}
